import { useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/useMobile";
import { useGlobalAgent } from "@/contexts/GlobalAgentContext";
import { UnifiedAgentChat } from "@/components/UnifiedAgentChat";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "motion/react";
import { Minus, Mic, Maximize2, Minimize2, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { unlockAudio, unlockHtmlAudio, isHtmlAudioReady, getPersistentAudio } from "@/lib/audioHelper";
import { FAB_INSET, FAB_SIZE, PANEL_MARGIN, Z, EXPAND_SPRING, COLLAPSE_TRANSITION, CONTENT_FADE } from "@/lib/overlay-constants";

const DEFAULT_SIZE = { width: 500, height: 500 };
const EXPANDED_SIZE = { width: 700, height: 650 };

export function GlobalAgentOverlay() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  const { state, minimizeAgent, restoreAgent, siteId, siteName, setSessionActive } = useGlobalAgent();

  const [size, setSize] = useState(DEFAULT_SIZE);
  const [isExpanded, setIsExpanded] = useState(false);

  const startSessionRef = useRef<(() => Promise<boolean>) | null>(null);
  const endSessionRef = useRef<(() => void) | null>(null);

  const handlePillClick = useCallback(async () => {
    restoreAgent();
    if (startSessionRef.current) {
      const success = await startSessionRef.current();
      if (success) setSessionActive(true);
    }
  }, [restoreAgent, setSessionActive]);

  const handleMinimize = useCallback(() => {
    if (endSessionRef.current) endSessionRef.current();
    setSessionActive(false);
    minimizeAgent();
  }, [minimizeAgent, setSessionActive]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => {
      setSize(!prev ? EXPANDED_SIZE : DEFAULT_SIZE);
      return !prev;
    });
  }, []);

  const [audioEnabled, setAudioEnabled] = useState(isHtmlAudioReady());
  const handleToggleAudio = useCallback(() => {
    if (audioEnabled) {
      const audio = getPersistentAudio();
      if (audio) audio.muted = true;
      setAudioEnabled(false);
    } else {
      unlockAudio();
      unlockHtmlAudio();
      const audio = getPersistentAudio();
      if (audio) audio.muted = false;
      setAudioEnabled(true);
    }
  }, [audioEnabled]);

  const isSiteManagerRoute = location.pathname.startsWith("/site");
  const isSiteManager = user?.role === "site_manager";

  if (!isSiteManagerRoute || !isSiteManager) return null;
  if (!state.isOpen) return null;

  // Mobile: Family-style expand from mic button (bottom-right)
  if (isMobile) {
    if (state.isMinimized) return null;
    const isAgentOpen = !state.isMinimized;

    return createPortal(
      <AnimatePresence>
        {isAgentOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40"
              style={{ zIndex: Z.backdrop }}
              onClick={handleMinimize}
            />

            {/* Panel — expands from mic button position (bottom-right) */}
            <motion.div
              className="fixed flex flex-col rounded-lg border border-border bg-background overflow-hidden"
              style={{ zIndex: Z.panel }}
              initial={{
                right: FAB_INSET,
                bottom: `calc(${FAB_INSET}px + env(safe-area-inset-bottom, 0px))`,
                width: FAB_SIZE,
                height: FAB_SIZE,
                borderRadius: 32,
                opacity: 0.8,
              }}
              animate={{
                left: PANEL_MARGIN,
                right: PANEL_MARGIN,
                bottom: `calc(${PANEL_MARGIN}px + env(safe-area-inset-bottom, 0px))`,
                width: `calc(100vw - ${PANEL_MARGIN * 2}px)`,
                height: "55vh",
                borderRadius: 12,
                opacity: 1,
                transition: EXPAND_SPRING,
              }}
              exit={{
                right: FAB_INSET,
                bottom: `calc(${FAB_INSET}px + env(safe-area-inset-bottom, 0px))`,
                left: "auto",
                width: FAB_SIZE,
                height: FAB_SIZE,
                borderRadius: 32,
                opacity: 0,
                transition: COLLAPSE_TRANSITION,
              }}
            >
              {/* Content — fades in after expand */}
              <motion.div
                {...CONTENT_FADE}
                className="flex flex-col h-full"
              >
                <div className="flex items-center justify-between px-lg py-sm border-b border-border shrink-0">
                  <span className="text-label">Concretly AI</span>
                  <Button variant="outline" size="sm" onClick={handleToggleAudio} className={cn("text-body-sm gap-2xs", audioEnabled && "text-success border-success/50")}>
                    <Volume2 className="h-3.5 w-3.5" />
                    {audioEnabled ? "Désactiver l'audio" : "Activer l'audio"}
                  </Button>
                </div>
                <UnifiedAgentChat
                  siteId={siteId}
                  siteName={siteName}
                  onMinimize={handleMinimize}
                  onStartSessionRef={(fn) => { startSessionRef.current = fn; }}
                  onEndSessionRef={(fn) => { endSessionRef.current = fn; }}
                  className="flex-1 min-h-0"
                  autoStartSession
                  hideHeader
                />
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body,
    );
  }

  // Desktop: fluid transition pill <-> panel
  return (
    <>
      <AnimatePresence mode="wait">
        {state.isMinimized ? (
          <motion.div
            key="pill"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="fixed z-40 bottom-8 left-1/2 -translate-x-1/2 pointer-events-auto"
          >
            <button
              onClick={handlePillClick}
              className={cn(
                "flex items-center gap-sm px-xl py-md rounded-full",
                "bg-background border border-border",
                "hover:border-primary/30 transition-colors cursor-pointer"
              )}
            >
              <Mic className="h-4 w-4 text-primary" />
              <span className="text-body-md">Concretly AI</span>
            </button>
          </motion.div>
        ) : (
          <div
            className="fixed inset-x-0 bottom-0 z-40 pointer-events-none flex justify-center items-end pb-2xl"
          >
            <motion.div
              key="panel"
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              style={{
                width: size.width,
                height: size.height,
              }}
              className={cn(
                "pointer-events-auto",
                "bg-background border border-border rounded-lg shadow-2xl",
                "overflow-hidden flex flex-col"
              )}
            >
            <div className="flex items-center justify-between px-lg py-md border-b border-border shrink-0">
              <div className="flex items-center gap-sm">
                <h3 className="text-subtitle">Concretly AI</h3>
                {siteName && <span className="text-body-md text-muted-foreground">• {siteName}</span>}
              </div>
              <div className="flex items-center gap-xs">
                <Button variant="ghost" size="icon" onClick={toggleExpanded} className="h-8 w-8 rounded-full" title={isExpanded ? "Réduire" : "Agrandir"} aria-label={isExpanded ? "Réduire" : "Agrandir"}>
                  {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={handleMinimize} className="h-8 w-8 rounded-full" title="Minimiser" aria-label="Minimiser">
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <UnifiedAgentChat
              siteId={siteId}
              siteName={siteName}
              onStartSessionRef={(fn) => { startSessionRef.current = fn; }}
              onEndSessionRef={(fn) => { endSessionRef.current = fn; }}
              className="flex-1 min-h-0"
              autoStartSession
              hideHeader
            />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
